<template>
  <v-app>
    <v-main>
      <v-container fluid>
        <div class="recruitment">
          <!-- Header curat de workspace (stil de referință CRM) -->
          <div class="recruitment__header">
            <div class="recruitment__header-left">
              <v-icon class="recruitment__header-icon" size="18">mdi-clipboard-text-search-outline</v-icon>
              <h1 class="recruitment__title">{{ $t("recruitment.list.title") }}</h1>
              <span class="recruitment__subtitle">{{ $t("recruitment.list.subtitle") }}</span>
            </div>
            <div class="recruitment__header-actions">
              <v-menu v-if="canManageEmailTemplate">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="tonal" prepend-icon="mdi-email-edit-outline">
                    {{ $t("recruitment.emailTemplate.action") }}
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-subheader v-if="companies.length > 1">
                    {{ $t("recruitment.emailTemplate.chooseWorkspace") }}
                  </v-list-subheader>
                  <v-list-item
                    v-for="company in companies"
                    :key="company.id"
                    :title="company.company_name"
                    @click="openTemplateDrawer(company.id)"
                  />
                </v-list>
              </v-menu>
              <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openAddDialog">
                {{ $t("recruitment.list.add") }}
              </v-btn>
            </div>
          </div>

          <v-card variant="flat" class="recruitment__panel">
            <!-- Filter bar: căutare + comutator vizualizare -->
            <div class="recruitment__filter-bar pa-4 pb-3">
              <v-text-field
                v-model="search"
                :label="$t('recruitment.list.search')"
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                class="recruitment__filter-search"
              />
              <NcrmSegmentedControl
                v-model="viewMode"
                :items="viewItems"
                :aria-label="$t('recruitment.list.view.ariaLabel')"
                class="recruitment__view-toggle"
              />
            </div>

            <v-divider />

            <div class="pa-4">
              <!-- Vizualizare carduri (anatomie de board-card) -->
              <div v-if="viewMode === 'cards'" class="recruitment__grid">
                <article
                  v-for="item in filteredRecords"
                  :key="item.id"
                  class="recruitment-card"
                  role="button"
                  tabindex="0"
                  @click="goToRequirementDetails(item.id)"
                  @keydown.enter.prevent="goToRequirementDetails(item.id)"
                  @keydown.space.prevent="goToRequirementDetails(item.id)"
                >
                  <div class="recruitment-card__main">
                    <div class="recruitment-card__chips">
                      <v-chip size="x-small" :color="item.requirement_type === 'client' ? 'primary' : 'neutral'">
                        {{ $t(`recruitment.requirementDrawer.${item.requirement_type === 'client' ? 'client' : 'internal'}`) }}
                      </v-chip>
                    </div>
                    <div class="recruitment-card__title">{{ item.name_vacancy || "—" }}</div>
                    <div class="recruitment-card__subtitle">{{ item.name_companies }}</div>
                    <div v-if="item.requirement_type === 'client'" class="recruitment-card__client">
                      <v-icon size="14">mdi-office-building-outline</v-icon>
                      <span>{{ item.client_company_name || "—" }}</span>
                    </div>
                  </div>

                  <div class="recruitment-card__meta">
                    <div class="recruitment-card__meta-row">
                      <v-icon size="14" class="recruitment-card__meta-icon">mdi-calendar-plus-outline</v-icon>
                      <span>{{ $t("recruitment.list.card.added") }}: {{ displayDate(item.added_at) }}</span>
                    </div>
                  </div>

                  <div class="recruitment-card__footer">
                    <div class="recruitment-card__footer-meta">
                      <v-icon size="13" class="recruitment-card__footer-icon">mdi-clock-outline</v-icon>
                      <span class="recruitment-card__footer-text">{{ displayDate(item.created_at) }}</span>
                    </div>
                    <div class="recruitment-card__actions">
                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        :title="$t('app.actions.edit')"
                        @click.stop="openEditDialog(item)"
                      >
                        <v-icon size="18" color="primary">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        :title="$t('app.actions.delete')"
                        @click.stop="confirmDeleteRecord(item.id)"
                      >
                        <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </article>
              </div>

              <!-- Vizualizare tabel (anatomie de tabel board) -->
              <v-data-table
                v-else
                :headers="columns"
                :items="filteredRecords"
                item-key="id"
                v-model:page="page"
                :items-per-page="itemsPerPage"
                class="recruitment__table"
              >
                <template #item="{ item }">
                  <tr class="recruitment__row" @click="goToRequirementDetails(item.id)">
                    <td>
                      <div class="recruitment__name-cell">
                        <v-icon size="14" class="recruitment__id-icon">mdi-office-building-outline</v-icon>
                        <span class="recruitment__company clickable" @click.stop="openDetailsInNewTab(item)">
                          {{ item.name_companies }}
                        </span>
                      </div>
                    </td>
                    <td>{{ item.name_vacancy }}</td>
                    <td>
                      <v-chip size="x-small" :color="item.requirement_type === 'client' ? 'primary' : 'neutral'">
                        {{ $t(`recruitment.requirementDrawer.${item.requirement_type === 'client' ? 'client' : 'internal'}`) }}
                      </v-chip>
                    </td>
                    <td>{{ item.client_company_name || "—" }}</td>
                    <td>{{ displayDate(item.added_at) }}</td>
                    <td>
                      <span class="recruitment__date">
                        <v-icon size="13" class="recruitment__date-icon">mdi-clock-outline</v-icon>
                        {{ displayDate(item.created_at) }}
                      </span>
                    </td>
                    <td class="recruitment__nav">
                      <v-icon size="18" class="recruitment__chevron">mdi-chevron-right</v-icon>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
          </v-card>
        </div>

        <RecruitmentRequirementDrawer
          v-model="requirementDrawerOpen"
          :record="activeRequirement"
          :companies="companies"
          @saved="handleRequirementSaved"
        />
        <RecruitmentEmailTemplateDrawer
          v-model="templateDrawerOpen"
          :company-id="templateCompanyId"
          @saved="notify($t('recruitment.emailTemplate.saved'), 'success')"
        />

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" location="bottom right">
          {{ snackbarMessage }}
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import RecruitmentRequirementDrawer from "@/components/recruitment/RecruitmentRequirementDrawer.vue";
import RecruitmentEmailTemplateDrawer from "@/components/recruitment/RecruitmentEmailTemplateDrawer.vue";
import { readStoredRecruitmentRoles } from "@/Utile/recruitmentSubmissions.mjs";

export default defineComponent({
  name: "Recrutare",
  components: {
    NcrmSegmentedControl,
    RecruitmentRequirementDrawer,
    RecruitmentEmailTemplateDrawer,
  },
  data() {
    return {
      requirements: [],
      companies: [],
      requirementDrawerOpen: false,
      activeRequirement: null,
      templateDrawerOpen: false,
      templateCompanyId: null,
      search: "",
      viewMode: "cards",
      page: 1,
      itemsPerPage: 15,
      hydrating: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },
  computed: {
    columns() {
      return [
        { key: "name_companies", title: this.$t("recruitment.list.table.company") },
        { key: "name_vacancy", title: this.$t("recruitment.list.table.position") },
        { key: "requirement_type", title: this.$t("recruitment.list.table.type"), sortable: false },
        { key: "client_company_name", title: this.$t("recruitment.list.table.client"), sortable: false },
        { key: "added_at", title: this.$t("recruitment.list.table.added"), sortable: false },
        { key: "created_at", title: this.$t("recruitment.list.table.updated"), sortable: false },
        { key: "row_nav", title: "", sortable: false, width: 48 },
      ];
    },
    // Comutator de vizualizare (carduri / tabel) pentru segmented control
    viewItems() {
      return [
        { value: "cards", label: this.$t("recruitment.list.view.cards"), icon: "mdi-view-grid-outline" },
        { value: "list", label: this.$t("recruitment.list.view.table"), icon: "mdi-view-list-outline" },
      ];
    },
    filteredRecords() {
      let records = this.requirements;
      if (this.search) {
        const searchTerm = this.search.toLowerCase();
        records = records.filter((item) =>
          [item.name_companies, item.name_vacancy, item.client_company_name]
            .filter(Boolean)
            .some((val) => String(val).toLowerCase().includes(searchTerm))
        );
      }
      return records;
    },
    canManageEmailTemplate() {
      const roles = readStoredRecruitmentRoles();
      return ["admin", "miniadmin", "hr", "vacation-hr"].some((role) => roles.has(role));
    },
  },
  watch: {
    search(newVal) {
      if (this.hydrating) return;
      this.page = 1;
      this.updateRouteQuery({ search: newVal || undefined, page: String(this.page) });
    },
    viewMode(newVal) {
      if (this.hydrating) return;
      this.updateRouteQuery({ view: newVal });
    },
    page(newVal) {
      if (this.hydrating) return;
      this.updateRouteQuery({ page: String(newVal) });
    }
  },
  created() {
    this.hydrating = true;
    const q = this.$route.query || {};
    if (q.page) {
      const p = parseInt(String(q.page), 10);
      if (!isNaN(p) && p > 0) this.page = p;
    }
    if (q.search) this.search = String(q.search);
    if (q.view) this.viewMode = String(q.view);
    this.fetchRequirements();
    this.fetchCompanies();
    this.$nextTick(() => { this.hydrating = false; });
  },
  mounted() {
    this.restoreScroll();
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const offset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() - offset);
      return date.toISOString().split("T")[0];
    },
    // Afișare dată în format business dd.mm.yyyy (design.md). Datele sunt normalizate la
    // yyyy-mm-dd în fetchRequirements; aici doar le reordonăm pentru afișare.
    displayDate(value) {
      if (!value) return "";
      const s = String(value);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}.${m[2]}.${m[1]}`;
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${dd}.${mm}.${d.getFullYear()}`;
    },
    goToRequirementDetails(id) {
      try { sessionStorage.setItem('recruitment:list:scrollY', String(window.scrollY || 0)); } catch (_) { /* ignore */ }
      this.$router.push({ name: "RecrutareDetails", params: { id } });
    },
    restoreScroll() {
      this.$nextTick(() => {
        let y = 0;
        try { y = Number(sessionStorage.getItem('recruitment:list:scrollY') || '0'); } catch (_) { /* ignore */ }
        if (y > 0) {
          window.scrollTo({ top: y, behavior: 'auto' });
          try { sessionStorage.removeItem('recruitment:list:scrollY'); } catch (_) { /* ignore */ }
        }
      });
    },
    updateRouteQuery(patch = {}) {
      const current = { ...this.$route.query };
      const merged = { ...current, ...patch };
      Object.keys(merged).forEach(k => {
        if (merged[k] === '' || merged[k] === undefined || merged[k] === null) delete merged[k];
      });
      const same = JSON.stringify(merged) === JSON.stringify(current);
      if (!same) this.$router.replace({ query: merged });
    },
    openDetailsInNewTab(item) {
      if (!item || !item.id) return;
      const { href } = this.$router.resolve({ name: "RecrutareDetails", params: { id: item.id } });
      try { window.open(href, "_blank"); } catch (_) { /* ignore */ }
    },
    openEditDialog(item) {
      this.activeRequirement = item;
      this.requirementDrawerOpen = true;
    },
    openAddDialog() {
      this.activeRequirement = null;
      this.requirementDrawerOpen = true;
    },
    openTemplateDrawer(companyId) {
      this.templateCompanyId = companyId;
      this.templateDrawerOpen = true;
    },
    handleRequirementSaved() {
      const wasEditing = Boolean(this.activeRequirement?.id);
      this.fetchRequirements();
      this.notify(
        this.$t(wasEditing ? "recruitment.list.alerts.updateSuccess" : "recruitment.list.alerts.addSuccess"),
        "success"
      );
    },
    confirmDeleteRecord(id) {
      if (confirm(this.$t("recruitment.list.alerts.deleteConfirm"))) {
        this.deleteRecord(id);
      }
    },
    deleteRecord(id) {
      axios.delete(`/candidati/recruitment_requirements/${id}`)
        .then(() => {
          this.fetchRequirements();
          this.notify(this.$t("recruitment.list.alerts.deleteSuccess"), "success");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea înregistrării:", error);
          this.notify(this.$t("recruitment.list.alerts.deleteError"), "error");
        });
    },
    fetchRequirements() {
      axios.get("/candidati/recruitment_requirements")
        .then((response) => {
          this.requirements = (response.data || [])
            .map((item) => ({
              ...item,
              added_at: this.formatDate(item.added_at),
              created_at: this.formatDate(item.created_at),
            }))
            .sort((a, b) => {
              const da = a.created_at ? new Date(a.created_at) : 0;
              const db = b.created_at ? new Date(b.created_at) : 0;
              return db - da; // DESC
            });
        })
        .catch((error) => {
          console.error("Eroare la obținerea cerințelor:", error);
        });
    },
    fetchCompanies() {
      axios.get("/candidati/companies")
        .then((res) => { this.companies = res.data || []; })
        .catch((err) => { console.error("Eroare la obținerea companiilor:", err); });
    },
    notify(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
});
</script>

<style scoped src="./Recrutare.css"></style>
